import { Version } from "./Version";
import { type IResult, UAParser } from "ua-parser-js";

export type Browser = "chrome" | "electron" | "firefox" | "opera" | "safari" | "edge" | "ie" | "samsung" | "unknown";

let userAgent: IResult | null = typeof window !== "undefined" ? UAParser(window.navigator.userAgent) : null;

export class BrowserDetector {
	private static _instance: BrowserDetector = new BrowserDetector(userAgent);

	private readonly _userAgent: IResult | null;
    private readonly _isThisAnEOLBrowser: boolean;
	private readonly _isThisALegacyBrowser: boolean;
	private readonly _isThisEdge: boolean;
	private readonly _isThisOpera: boolean;
	private readonly _isThisFirefox: boolean;
	private readonly _isThisChromeOrElectron: boolean;
	private readonly _isThisChrome: boolean;
	private readonly _isThisElectron: boolean;
	private readonly _isThisSafari: boolean;
	private readonly _isThisSamsung: boolean;
	private readonly _isMobile: boolean;
    private readonly _isThisInternetExplorer: boolean;
	private readonly _isThisRunningIOS13OrLower: boolean;

	constructor(userAgent: IResult | null) {
		this._userAgent = userAgent;
        this._isThisAnEOLBrowser = !!userAgent && BrowserDetector.isEOLBrowser(userAgent);
		this._isThisALegacyBrowser = !!userAgent && BrowserDetector.isLegacyBrowser(userAgent);
		this._isThisEdge = !!userAgent && BrowserDetector.isEdge(userAgent);
		this._isThisOpera = !!userAgent && BrowserDetector.isOpera(userAgent);
		this._isThisFirefox = !!userAgent && BrowserDetector.isFirefox(userAgent);
		this._isThisChromeOrElectron = !!userAgent && BrowserDetector.isChromeOrElectron(userAgent);
		this._isThisChrome = !!userAgent && BrowserDetector.isChrome(userAgent);
		this._isThisElectron = !!userAgent && BrowserDetector.isElectron(userAgent);
		this._isThisSafari = !!userAgent && BrowserDetector.isSafari(userAgent);
		this._isThisSamsung = !!userAgent && BrowserDetector.isSamsungBrowser(userAgent);
        this._isThisInternetExplorer = !!userAgent && BrowserDetector.isInternetExplorer(userAgent);
		this._isMobile = !!userAgent && BrowserDetector.isMobile(userAgent);
		this._isThisRunningIOS13OrLower = !!userAgent && BrowserDetector.isRunningIOS13OrLower(userAgent);
	}

	static setInstance(browserDetector: BrowserDetector) {
		this._instance = browserDetector;
	}

    static isThisAnEOLBrowser() {
        return BrowserDetector._instance._isThisAnEOLBrowser;
    }

	static isThisALegacyBrowser() {
		return BrowserDetector._instance._isThisALegacyBrowser;
	}

	static isThisEdge() {
		return BrowserDetector._instance._isThisEdge;
	}

    static isThisInternetExplorer() {
        return BrowserDetector._instance._isThisInternetExplorer;
    }

	static isThisFirefox() {
		return BrowserDetector._instance._isThisFirefox;
	}

	static isThisChromeOrElectron() {
		return BrowserDetector._instance._isThisChromeOrElectron;
	}

	static isThisChrome() {
		return BrowserDetector._instance._isThisChrome;
	}

	static isThisElectron() {
		return BrowserDetector._instance._isThisElectron;
	}

	static isThisSafari() {
		return BrowserDetector._instance._isThisSafari;
	}

	static isThisSamsung() {
		return BrowserDetector._instance._isThisSamsung;
	}

	static isThisOpera() {
		return BrowserDetector._instance._isThisOpera;
	}

	static isThisMobile() {
		return BrowserDetector._instance._isMobile;
	}

	static get thisBrowserType(): Browser {
		if (BrowserDetector.isThisChrome()) return "chrome";
		if (BrowserDetector.isThisElectron()) return "electron";
		if (BrowserDetector.isThisFirefox()) return "firefox";
		if (BrowserDetector.isThisEdge()) return "edge";
		if (BrowserDetector.isThisSafari()) return "safari";
		if (BrowserDetector.isThisSamsung()) return "samsung";
		if (BrowserDetector.isThisOpera()) return "opera";
        if (BrowserDetector.isThisInternetExplorer()) return "ie";
		return "unknown";
	}

	/**
	 * Watch out: if the browser is an electron app, this returns the electron version, not the underlying Chromium version
	 */
	static get thisBrowserMajorVersion(): number | null {
		const { _userAgent } = BrowserDetector._instance;
		if (!_userAgent) {
			return null;
		}
		return BrowserDetector.getBrowserMajorVersion(_userAgent);
	}

	static isThisRunningIOS13OrLower() {
		return BrowserDetector._instance._isThisRunningIOS13OrLower;
	}

    //Redirect to EOL page (unsupported.html)
    static isEOLBrowser(userAgent: IResult): boolean {
        if (BrowserDetector.isElectron(userAgent)) {
            return false;
        }
        
        return (
            (BrowserDetector.isInternetExplorer(userAgent)) ||
            (BrowserDetector.isEdge(userAgent) && BrowserDetector.getEdgeVersion(userAgent) < 72) ||
            (BrowserDetector.isChrome(userAgent) && BrowserDetector.getChromeVersion(userAgent) < 71) ||
            (BrowserDetector.isFirefox(userAgent) && BrowserDetector.getFirefoxVersion(userAgent) < 65) ||
            (BrowserDetector.isOpera(userAgent) && BrowserDetector.getOperaVersion(userAgent) < 58) ||
            (BrowserDetector.isSafari(userAgent) && 
                (!BrowserDetector.isMobile(userAgent) && BrowserDetector.getSafariVersion(userAgent).lt(new Version(12, 1, 0))) || 
                (BrowserDetector.isMobile(userAgent) && BrowserDetector.getSafariVersion(userAgent).lt(new Version(11, 3, 0)))
            )
        );
    }

	static isLegacyBrowser(userAgent: IResult): boolean {
		if (BrowserDetector.isElectron(userAgent)) {
			return false;
		}

		return (
			(BrowserDetector.isChrome(userAgent) && BrowserDetector.getChromeVersion(userAgent) < 76) ||
			(BrowserDetector.isFirefox(userAgent) && BrowserDetector.getFirefoxVersion(userAgent) < 121) ||
			(BrowserDetector.isOpera(userAgent) && BrowserDetector.getOperaVersion(userAgent) < 58) ||
			(BrowserDetector.isSafari(userAgent) && BrowserDetector.getSafariVersion(userAgent).major < 15) ||
			(BrowserDetector.isSamsungBrowser(userAgent) && BrowserDetector.getSamsungBrowserVersion(userAgent).lt(new Version(10, 1, 0)))
		);
	}
    
	static getBrowserMajorVersion(userAgent: IResult): number {
		const { browser } = userAgent;
		const { major } = browser;
		return major ? parseInt(major) : 0;
	}

	static getBrowserVersion(userAgent: IResult): Version {
		const { browser } = userAgent;
		const { version } = browser;

		if (!version) {
			return new Version(0, 0, 0);
		}

		const [majorPart, minorPart, patchPart] = version.split(".");
		const major = parseInt(majorPart);
		const minor = minorPart !== undefined ? parseInt(minorPart) : null;
		const patch = patchPart !== undefined ? parseInt(patchPart) : null;

		return new Version(major, minor, patch);
	}

	static getOsVersion(userAgent: IResult): Version {
		const { os } = userAgent;
		const { version } = os;

		if (!version) {
			return new Version(0, 0, 0);
		}

		const [majorPart, minorPart, patchPart] = version.split(".");
		const major = parseInt(majorPart);
		const minor = minorPart !== undefined ? parseInt(minorPart) : null;
		const patch = patchPart !== undefined ? parseInt(patchPart) : null;

		return new Version(major, minor, patch);
	}

	static getChromeVersion(userAgent: IResult): number {
		return BrowserDetector.getBrowserMajorVersion(userAgent);
	}

    static getEdgeVersion(userAgent: IResult): number {
        return BrowserDetector.getBrowserMajorVersion(userAgent);
    }
    
	static getFirefoxVersion(userAgent: IResult): number {
		return BrowserDetector.getBrowserMajorVersion(userAgent);
	}

	static getOperaVersion(userAgent: IResult): number {
		return BrowserDetector.getBrowserMajorVersion(userAgent);
	}

	static getSafariVersion(userAgent: IResult): Version {
		return BrowserDetector.getBrowserVersion(userAgent);
	}

	static getSamsungBrowserVersion(userAgent: IResult): Version {
		return BrowserDetector.getBrowserVersion(userAgent);
	}

	static isEdge(userAgent: IResult): boolean {
		return userAgent.browser.name === "Edge";
	}

	static isFirefox(userAgent: IResult): boolean {
		return userAgent.browser.name === "Firefox";
	}

	static isChromeOrElectron(userAgent: IResult): boolean {
		return BrowserDetector.isChrome(userAgent) || BrowserDetector.isElectron(userAgent);
	}

	static isChrome(userAgent: IResult): boolean {
		return userAgent.browser.name === "Chrome";
	}

	static isElectron(userAgent: IResult): boolean {
		return userAgent.browser.name === "Electron";
	}

	static isOpera(userAgent: IResult): boolean {
		return userAgent.browser.name === "Opera";
	}

	static isSafari(userAgent: IResult): boolean {
		return userAgent.browser.name === "Safari";
	}

    static isInternetExplorer(userAgent: IResult): boolean {
        return userAgent.browser.name === "IE";
    }

	static isSamsungBrowser(userAgent: IResult): boolean {
		return !!userAgent.browser.name && userAgent.browser.name.includes("Samsung");
	}

	static isMobile(userAgent: IResult) {
		const { device } = userAgent;
		const { type } = device;

		if (typeof type === "undefined") {
			return false;
		}

		return type === "mobile" || type === "tablet";
	}

	static isIOS(userAgent: IResult): boolean {
		const { os } = userAgent;
		const { name } = os;
		return name === "iOS";
	}

	static isRunningIOS13OrLower(userAgent: IResult) {
		return BrowserDetector.isIOS(userAgent) && BrowserDetector.getOsVersion(userAgent).lte(new Version(13, 0, 0));
	}
}

// Asynchronously perform a feature check and re-initialize the BrowserDetector with this new information when it arrives
(async () => {
	userAgent = userAgent ? await userAgent.withFeatureCheck() : null;
	BrowserDetector.setInstance(new BrowserDetector(userAgent));
})();
